import { z } from 'zod';
import { BooleanQuery } from './common';

/**
 * A equipe do tenant para os seletores de pessoa (responsável do ticket e da
 * atividade agendada, transferência de conversa, filtros por atendente…) —
 * `GET /users/team`. Pedido da PoolTrack (05/10): o "Responsável" do ticket
 * listava os logins de portal das associações misturados à equipe, e a lista
 * vinha de `GET /users?limit=200`, que é GLOBAL (todas as empresas, portal
 * incluído) e corta em 200 por nome.
 *
 * A regra de quem é "equipe deste tenant" mora na API (shared/db/tenant-team):
 * papel admin/attendant E membro do tenant OU atendente do espelho do TiFlux
 * do tenant.
 */
export const TEAM_ROLES = ['admin', 'attendant'] as const;
export const TeamRoleSchema = z.enum(TEAM_ROLES);
export type TeamRole = z.infer<typeof TeamRoleSchema>;

/** os mesmos rótulos da tela de usuários */
export const TEAM_ROLE_LABELS: Record<TeamRole, string> = {
  admin: 'Admin',
  attendant: 'Atendente',
};

/** quantas pessoas o seletor mostra de cada vez (o mesmo do seletor de técnico) */
export const TEAM_PICKER_LIMIT = 30;
/** teto da lista inteira — filtros e listas de marcar pedem a equipe toda */
export const TEAM_LIST_MAX = 200;

export const TeamListQuerySchema = z.object({
  /** nome ou e-mail, sem acento e sem caixa */
  q: z.string().trim().max(120).optional(),
  /** sem ele, só os ativos */
  include_inactive: BooleanQuery.optional(),
  limit: z.coerce.number().int().min(1).max(TEAM_LIST_MAX).default(TEAM_PICKER_LIMIT),
});
export type TeamListQuery = z.infer<typeof TeamListQuerySchema>;

export const TeamMemberSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  email: z.string(),
  role: TeamRoleSchema,
  active: z.boolean(),
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

export const TeamListResponseSchema = z.object({
  items: z.array(TeamMemberSchema),
  /** havia mais gente além do `limit` — a tela pede para refinar a busca */
  hasMore: z.boolean(),
});
export type TeamListResponse = z.infer<typeof TeamListResponseSchema>;

/** a linha de baixo do seletor: "Atendente · ana@pooltrack.com.br" */
export function teamMemberSecondary(m: Pick<TeamMember, 'role' | 'email'>): string {
  return [TEAM_ROLE_LABELS[m.role], m.email].filter((p) => p && p.trim() !== '').join(' · ');
}

/**
 * Os códigos das escritas que escolhem uma pessoa da equipe (422): o
 * responsável (ticket, atividade agendada, automação, transferência de
 * conversa) e as listas de pessoas (grupos, permissões, cofre, agenda).
 *
 * F-178: `target_inactive` (409) é a mesma escolha recusada porque a pessoa
 * está DESLIGADA nesta empresa. A lista da tela pode ser de antes do
 * desligamento — o rótulo diz o que fazer.
 */
export const TEAM_ERROR_LABELS: Record<string, string> = {
  responsible_not_in_team:
    'O responsável precisa ser alguém da equipe — login do portal ou de outra empresa não pode.',
  user_not_in_team:
    'Só pessoas da equipe podem ser escolhidas aqui — login do portal ou de outra empresa não.',
  target_inactive:
    'Esta pessoa foi desligada nesta empresa e não pode ser escolhida — atualize a página e escolha outra.',
};
